<template>
  <div>
    <!-- Page Background -->
    <div class="min-h-screen bg-gray-100">
      <!-- Header Section -->
      <section class="pt-12 pb-6 bg-gradient-to-br bg-gray-200">
        <div class="container mx-auto px-4">
          <!-- Heading and Breadcrumb -->
          <div>
            <h4 class="text-primary-second text-3xl font-bold">Search</h4>
            <div class="flex items-center text-grey mb-2 mt-1 text-base font-light">
              <router-link class="hover:underline text-grey" to="/" title="Home">Home</router-link>
              <span class="mx-2">&gt;</span>
              <span class="text-grey">Search</span>
            </div>
          </div>
          <!-- Search + Filter + Sort Row -->
          <div class="w-full mt-4">
            <div class="flex flex-col md:flex-row items-center gap-4 w-full">

              <!-- Search Box -->
              <div class="relative flex items-center bg-white rounded-full shadow-md px-4 py-2 w-full md:w-8/12">
                <input @input="search = search.trimStart()" @keyup.enter="clickOnSearch" type="text" v-model="search"
                  placeholder="Search by Doctors, Establishments, Specialities, Locations"
                  aria-label="Search doctors"
                  class="flex-1 border-0 bg-transparent outline-none text-base" />
                <button @click="clickOnSearch" type="button"
                  class="ml-2 flex items-center justify-center w-10 h-10 rounded-full gradnt-btn">
                  <FeatherIcon type="search" class="text-white w-4 h-4" />
                </button>

              <div v-if="suggestionsOpen" class="bg-white p-4 px-2 absolute left-0 top-full mt-2 w-full rounded-2xl shadow-md max-h-[60vh] overflow-y-auto"
                style="z-index: 9999">
                <template v-if="suggestions.establishments.length || suggestions.providers.length || suggestions.specialities.length ||
                  suggestions.locations.length">

                  <ul v-if="suggestions.establishments.length >= 1">
                    <p class="text-base font-bold text-primary pl-5">
                      Establishments
                    </p>
                    <li v-for="(item, index) in suggestions.establishments" :key="index"
                      @click="goToEstDetails(item.id)" style="background-color: white"
                      class="flex items-center px-5 py-2 text-sm capitalize border-b-2 border-0 cursor-pointer border-solid border-grey-light">
                      {{ item.name }}
                    </li>
                  </ul>
                  <ul v-if="suggestions.providers.length">
                    <p class="text-base font-bold text-primary pt-3 pl-5 pb-1">
                      Doctors
                    </p>
                    <li v-for="(item, index) in suggestions.providers" :key="index"
                      @click="goToProviderDetails(item.id)" style="background-color: white"
                      class="flex items-center px-5 py-2 text-sm capitalize border-b-2 border-0 cursor-pointer border-solid border-grey-light">
                      <img
                        :src="item.avatar || 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png'"
                        :alt="item.firstName" class="w-8 h-8 mr-2 rounded-full" /> {{ item.firstName }}
                      {{ item.lastName }}
                    </li>
                  </ul>
                  <ul v-if="suggestions.specialities.length">
                    <p class="text-base font-bold text-primary pt-3 pl-5 pb-1">
                      Specialities
                    </p>
                    <li v-for="(item, index) in suggestions.specialities" :key="index"
                      @click="searchDoctor(item.specialities)" style="background-color: white"
                      class="flex items-center px-5 py-2 text-sm capitalize border-b-2 border-0 cursor-pointer border-solid border-grey-light">
                      {{ String(item.specialities || '').split(',').map((x) => x.trim()).filter(Boolean).join(', ') }}
                    </li>
                  </ul>
                  <ul v-if="suggestions.locations.length >= 1">
                    <p class="text-base font-bold text-primary pt-3 pl-5 pb-1">
                      Locations
                    </p>
                    <li v-for="(item, index) in suggestions.locations" :key="index" @click="
                      $router.push({
                        name: 'search',
                        params: { searchText: item.address }
                      })
                      "
                      class="flex items-center px-5 py-2 text-sm capitalize border-b-2 cursor-pointer border-solid border-grey-light">
                      {{ item.address }}
                    </li>
                  </ul>
                </template>
                <div v-else class="text-center font-semibold text-black">
                  No Data Available
                </div>
              </div>
              </div>

              <!-- Filter Button -->
              <button @click="openFilterPopup"
                class="text-white px-6 py-3 gradnt-btn rounded-full flex items-center justify-center text-base shadow-md">
                <img class="w-5 mr-2" src="@/assets/images/filter.svg" />
                Filter
              </button>




              <!-- Sort Dropdown -->
              <div class="relative inline-block text-left" ref="dropdownRef">
                <button @click="toggleDropdown"
                  class="px-6 py-3 gradnt-btn text-white rounded-full flex items-center shadow-md">
                  {{ selectedLabel }}
                  <FeatherIcon type="chevron-down" class="ml-2 w-4 h-4" />
                </button>

                <!-- Dropdown -->
                <transition name="fade-slide">
                  <div v-if="open"
                    class="absolute right-0 mt-2 w-10/12 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 overflow-hidden">
                    <button :disabled="selectedSort === 'availability'"
                      :class="selectedSort !== 'availability' ? 'block px-4 py-2 text-sm w-full text-left hover:bg-primary hover:text-white' : 'hidden'"
                      @click="selectOption('availability')">
                      Availability
                    </button>
                    <button :disabled="selectedSort === 'distance'"
                      :class="selectedSort !== 'distance' ? 'block px-4 py-2 text-sm w-full text-left hover:bg-primary hover:text-white' : 'hidden'"
                      @click="selectOption('distance')">
                      Distance
                    </button>
                    <button :disabled="selectedSort === 'price'"
                      :class="selectedSort !== 'price' ? 'block px-4 py-2 text-sm w-full text-left hover:bg-primary hover:text-white' : 'hidden'"
                      @click="selectOption('price')">
                      Price
                    </button>
                  </div>
                </transition>
              </div>



            </div>
          </div>



          <!-- Tabs -->
          <div class="flex justify-center mt-8">
            <div class="flex bg-white rounded-full shadow p-1 w-full max-w-md">
              <button @click="activeTab = 'list'" :class="[
                'flex-1 py-2 rounded-full font-semibold transition',
                activeTab === 'list'
                  ? 'bg-primary text-white shadow'
                  : 'bg-white text-primary'
              ]">
                List
              </button>
              <button @click="activeTab = 'map'" :class="[
                'flex-1 py-2 rounded-full font-semibold transition',
                activeTab === 'map'
                  ? 'bg-primary text-white shadow'
                  : 'bg-white text-primary'
              ]">
                Map
              </button>
            </div>
          </div>
          <!-- TB-107: where the search is limited to, and a clear way out -->
          <div v-if="locationFilter" class="flex justify-center mt-4" data-state="location-filter">
            <span class="inline-flex items-center gap-2 bg-white rounded-full shadow px-4 py-1 text-sm text-primary">
              <FeatherIcon type="map-pin" class="w-4 h-4" /> Near {{ locationFilter }}
              <button type="button" class="text-gray-500 hover:text-primary" aria-label="Clear location" @click="clearLocation">&times;</button>
            </span>
          </div>
        </div>
      </section>

      <!-- Tabs Section -->
      <section>
        <div class="container mx-auto px-4">
          <div class="w-full">
            <div v-if="activeTab === 'list'">
              <!-- TB-109: skeleton cards while searching, then clean result cards -->
              <div v-if="searchState === 'loading' && !doctors.length" class="tb-results mt-8" data-testid="search-skeleton" role="status" aria-busy="true">
                <span class="sr-only">Searching doctors</span>
                <div v-for="n in 4" :key="n" class="tb-skel-card" aria-hidden="true">
                  <div class="flex gap-4 items-center">
                    <span class="tb-skel tb-skel--avatar"></span>
                    <div class="flex-1 flex flex-col gap-2">
                      <span class="tb-skel" style="width: 55%; height: 1rem"></span>
                      <span class="tb-skel" style="width: 75%"></span>
                    </div>
                  </div>
                  <div class="flex gap-2 mt-6">
                    <span v-for="m in 3" :key="m" class="tb-skel" style="width: 4.5rem; height: 2.1rem"></span>
                  </div>
                </div>
              </div>
              <p v-if="searchState === 'done' && (doctors.length || establishments.length)" class="tb-results-summary mt-6" data-testid="results-summary">
                {{ resultsSummary }}
              </p>
              <div v-if="doctors.length" class="tb-results mt-4">
                <DoctorResultCard
                  v-for="item in doctors"
                  :key="item.id"
                  :doctor="cardFor(item)"
                  :next="nextById[item.id] || null"
                  @visible="loadNext(item)"
                  @open="openDoctor(item.id, $event)"
                />
              </div>

              <div v-if="establishments?.length > 0" class="tb-results mt-8">
                <article v-for="est in establishments" :key="est.id" class="tb-est-card">
                  <img class="tb-est-card__avatar" :src="est.avatar || 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png'" alt="" />
                  <div class="flex-1 min-w-0">
                    <h3 class="tb-est-card__name">{{ est.name }}</h3>
                    <p class="tb-est-card__sub">{{ estSubtitle(est) }}</p>
                  </div>
                  <button type="button" class="tb-est-card__view" @click="goToEstDetails(est.id)">View clinic</button>
                </article>
              </div>
              <!-- TB-107: a clear no-results state instead of an error toast -->
              <div
                v-if="searchState === 'done' && !doctors.length && !establishments.length"
                class="text-center py-12"
                data-state="no-results"
                role="status"
              >
                <p class="text-xl text-primary-second font-semibold mb-2">No doctors found</p>
                <p class="text-gray-500 mb-4">
                  Try a doctor's first or last name, a speciality such as cardiology, or a clinic name{{ locationFilter ? ', or clear the location' : '' }}.
                </p>
                <button type="button" class="px-6 py-2 rounded-full bg-primary text-white font-medium" @click="showAllDoctors">
                  Show all doctors
                </button>
              </div>
              <div v-else-if="searchState === 'error'" class="text-center py-12" data-state="search-error" role="status">
                <p class="text-xl text-primary-second font-semibold mb-2">Search is not responding</p>
                <p class="text-gray-500 mb-4">Check your connection and try again.</p>
                <button type="button" class="px-6 py-2 rounded-full bg-primary text-white font-medium" @click="searchDoctor(search)">
                  Try again
                </button>
              </div>
              <!-- <div class="text-center mt-4 mb-4">
                <button
                  v-if="visibleCount < filteredDoctors?.length"
                  @click="visibleCount += 6"
                  class="bg-white border-0 cursor-pointer font-medium font-poppins py-3 rounded-full shadow-md text-base text-center w-48 mx-auto"
                >
                  Show More
                </button>
              </div> -->
            </div>
            <div v-if="activeTab === 'map'" class="mt-6" data-testid="map-tab">
              <!-- TB-109-C: Clinical Calm map: a card popup on each pin (name, speciality,
                   next free time, View profile) and the same doctors listed beside the map.
                   TB-107 pin behaviour (fit to all pins, one pin per location) is unchanged. -->
              <p v-if="searchState === 'done' && mapDoctors.length" class="tb-results-summary mb-3" data-testid="map-summary">{{ mapSummary }}</p>
              <div class="tb-map-layout">
              <div class="tb-search-map" :data-pin-count="markers.length" :data-pin-ids="markers.map((m) => m.id).join(',')">
              <GMapMap ref="gmapRef" :center="cleanedCenter" :zoom="mapZoom" :options="mapOptions" style="width: 100%; height: min(560px, 65vh)">
                <GMapMarker v-for="(marker, idx) in cleanedMarkers" :key="marker.key || idx" :position="marker.position"
                  :clickable="true" :icon="{ url: require('@/assets/images/pin.png') }" @click="toggleInfoWindow(idx)">
                  <GMapInfoWindow v-if="currentMidx === idx && infoWinOpen" :options="infoOptions"
                    :position="infoWindowPos" :opened="infoWinOpen" @closeclick="infoWinOpen = false">
                    <MapPinCard
                      v-if="marker.info"
                      :doctor="cardFor(marker.info)"
                      :next="nextById[marker.id] || null"
                      @open="openDoctor(marker.id, $event)"
                    />
                  </GMapInfoWindow>
                </GMapMarker>
              </GMapMap>
              </div>
              <ul v-if="mapDoctors.length" class="tb-map-list" aria-label="Doctors on the map" data-testid="map-list">
                <li v-for="d in mapDoctors" :key="d.id">
                  <div
                    role="button" tabindex="0" class="tb-map-list__item"
                    :aria-pressed="activeMapDoctorId === d.id ? 'true' : 'false'"
                    :data-map-doctor="d.id"
                    @click="focusPin(d.id)"
                    @keydown.enter.prevent="focusPin(d.id)"
                    @keydown.space.prevent="focusPin(d.id)"
                  >
                    <MapPinCard row :active="activeMapDoctorId === d.id" :doctor="cardFor(d.info)" :next="nextById[d.id] || null" />
                  </div>
                </li>
              </ul>
              </div>
              <div v-if="searchState === 'done' && !markers?.length" class="text-center text-gray-400 mt-8" data-state="map-empty">
                No doctors found on the map.
              </div>
              <!-- TB-107: doctors without a map location are listed, not hidden -->
              <div v-if="withoutLocation.length" class="mt-4 tb-map-note" data-state="no-map-location">
                <p class="text-sm mb-2">
                  {{ withoutLocation.length === 1 ? '1 doctor has' : withoutLocation.length + ' doctors have' }} no map location yet:
                </p>
                <ul class="flex flex-wrap gap-2">
                  <li v-for="doc in withoutLocation" :key="doc.id">
                    <button type="button" class="tb-map-note__doc" @click="goToDetails(doc.id)">{{ doc.name }}</button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!--Filter PopUP -->
    <transition name="fade">
      <div v-if="filterPopup"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 rounded-3xl">
        <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative mx-2 flex flex-col max-h-[90vh]">
          <button @click="
            () => {
              ; (filterPopup = false), (isFilter = false)
            }
          " class="absolute top-3 right-3 text-gray-400 hover:text-primary text-2xl font-bold">
            &times;
          </button>
          <h2 class="text-2xl font-bold mb-4">Search Filter</h2>
          <div class="flex-1 overflow-y-auto flex flex-col gap-5">
            <div class="pt-2 grid grid-cols-2 gap-2 px-2">
              <flat-pickr
                class="bg-white border-0 font-poppins cursor-pointer shadow-md py-2  flex items-center rounded-full text-base text-center"
                placeholder="Date" v-model="date" :config="config" />
              <TimePicker :key="timeKey" v-model="time" :minute-interval="30" format="hh:mm A" :hour-range="hourRange"
                placeholder="Start Time"
                input-class="bg-white border-0 font-poppins cursor-pointer shadow-md py-5 px-4 flex items-center rounded-full text-base w-64 text-center" />

              <!-- <flat-pickr
                class="bg-white border-0 font-poppins cursor-pointer shadow-md py-2 px-4 flex items-center rounded-full text-base w-1/2 text-center"
                :config="configdateTimePicker"
                v-model="time"
                placeholder="Time"
              /> -->
            </div>
            <!-- <div class="flex justify-center items-center space-x-1">
              <FeatherIcon type="info" class="w-4 h-4"/>
              <span class="text-sm">Time results will displayed on selected time & onwards</span>
              </div> -->
            <h3 class="font-bold text-xl py-3 text-left text-primary-second">
              Type of Consultation
            </h3>
            <div class="grid grid-cols-12 gap-4">
              <button v-for="(consultation, index) in consultationTypes" :key="index"
                v-on:click="selectConsultationType(consultation.id)" :class="selectedconsultationID.includes(consultation.id)
                    ? 'gradnt-btn text-white'
                    : 'bg-none text-black'
                  "
                class="bg-grey-light opacity-75 font-poppins border-0 px-8 cursor-pointer shadow-md py-3 flex items-center mx-2 justify-center rounded-full text-base col-span-6 uppercase">
                {{ consultation.nameEn }}
              </button>
            </div>
            <h3 class="font-bold text-xl my-8 text-left px-4">
              Purpose of consultation
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4">
              <button v-for="(poc, index) in purpose_of_consultaion" :key="index" v-on:click="selectedPoc(poc.id)"
                :class="`
            bg-grey-light
            opacity-75
            font-poppins
            border-0
            px-8
            cursor-pointer
            shadow-md
            py-3
            flex
            items-center
            mx-2
            justify-center
            rounded-full
            text-base
            uppercase
           ${selectedID.includes(poc.id)
                    ? 'gradnt-btn text-white'
                    : 'border-white'
                  }`">
                {{ poc.title }}
              </button>
            </div>
          </div>
          <div class="flex justify-center items-center mt-6 gap-4 pt-2">
            <button @click="clear"
              class="bg-white border-0 w-1/3 justify-center opacity-75 font-poppins cursor-pointer shadow-lg py-2 flex items-center rounded-full text-base">
              Clear All
            </button>
            <button @click="searchDoctor(search)"
              class="gradnt-btn text-white border-0 w-1/3 justify-center font-poppins cursor-pointer shadow-lg py-2 flex items-center rounded-full text-base">
              Apply
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { personName } from '@/utils/booking'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { GMapMap, GMapMarker, GMapInfoWindow } from '@fawmi/vue-google-maps'
import MapPinCard from '@/components/search/MapPinCard.vue'
import DoctorResultCard from '@/components/search/DoctorResultCard.vue'
import { createLimiter, findNextAvailable, groupSlots, isNoSlotsError, scheduleDates, toIsoDate } from '@/utils/slotPicker'
import { formatMoney } from '@/utils/booking'
import moment from 'moment'
import { clinicToday, wallTime, formatClock, zoneLabel } from '@/utils/locale'
import { DEFAULT_WINDOW } from '@/utils/slots'
import { toMin, fromMin } from '@/utils/schedule'
import { useNavigationStore } from '../../store/navigation'
import debounce from 'lodash/debounce'
import { lookupGet } from '@/utils/sharedGet'
import TimePicker from 'vue3-timepicker'
import 'vue3-timepicker/dist/VueTimepicker.css'
import { logEvent } from 'firebase/analytics'
import { analytics } from '../../firebase'
import { resolvedTheme } from '@/theme/theme.js'
import { applyMapBackground, mapStylesFor } from '../../config/mapStyles'
import { BASE_MAP_OPTIONS, DEFAULT_ZOOM } from '../../config/map'
import { useLocationStore } from '../../store/location'
import { doctorTitle } from '@/utils/displayName'
import { activeVisitTypes } from '@/utils/visitTypes'
import {
  normalizeSearchTerm,
  mapPinsFor,
  pinBounds,
  createLatestGate,
  isCancelled
} from '@/utils/doctorSearch'

const MAX_FIT_ZOOM = 14

export default {
  name: 'UserSearch',

  data() {
    return {
      open: false,
      selectedSort: 'availability',
      activeTab: 'list',
      activeSlotTab: 'today',
      search: '',
      date: null,
      time: null,
      timeKey: 0,
      configdateTimePicker: {
        enableTime: true,
        enableSeconds: true,
        noCalendar: true
      },
      filterPopup: false,
      // TB-063: the patient's fix, or the Bahrain centre (never 0,0)
      center: { ...useLocationStore().coords },
      mapZoom: DEFAULT_ZOOM,
      infoContent: {},
      infoWindowPos: null,
      infoWinOpen: false,
      currentMidx: null,
      pinIcon: '@/assets/images/pin.png',
      infoOptions: {
        pixelOffset: { width: 0, height: -35 },
        maxWidth: 320
      },
      markers: [],
      // TB-107: doctors in the results with no Bahrain map location
      withoutLocation: [],
      // idle | loading | done | error
      searchState: 'idle',
      locationFilter: '',
      searchGate: createLatestGate(),
      suggestGate: createLatestGate(),
      suggestionsOpen: false,
      suggestions: [],
      weeks: [],
      searchSeq: 0,
      // TB-109: per doctor { status, date, slots } for the card's next free times
      nextById: {},
      slotLimiter: createLimiter(4),
      doctors: [],
      establishments: [],
      consultationTypes: [],
      purpose_of_consultaion: [],
      selectedconsultationID: [],
      selectedID: [],
      isFilter: false,
      suppressSuggestions: false,
      lastSearchedTerm: null,
      hourRange: [
        '8a',
        '9a',
        '10a',
        '11a',
        '12p',
        '1p',
        '2p',
        '3p',
        '4p',
        '5p'
      ],
      config: {
        dateFormat: 'Y-m-d',
        minDate: clinicToday() // TB-123: the clinic's today, not the device's
      }
    }
  },
  watch: {
    // backgroundColor only applies at map creation; repaint on a theme switch.
    mapOptions () {
      const map = this.$refs.gmapRef
      if (map && map.$mapPromise) map.$mapPromise.then((m) => applyMapBackground(m, resolvedTheme.value)).catch(() => {})
    },
    search: {
      handler: debounce(function (val) {
        // a search run from the route sets the box too; only typing opens suggestions
        if (this.suppressSuggestions || val === this.lastSearchedTerm) {
          this.suggestions = []
          this.suggestionsOpen = false
          return
        }
        const term = normalizeSearchTerm(val)
        if (term.length >= 2) {
          // TB-107: only the latest suggestion request may answer
          const gate = this.suggestGate.next()
          this.$http
            .get(`${process.env.VUE_APP_PROVIDERS_SERVICE_API}suggestions`, {
              params: { search: term },
              signal: gate.signal
            })
            .then((res) => {
              if (!gate.isCurrent()) return
              this.suggestionsOpen = true
              this.suggestions = res.data.data
            })
            .catch(() => {})
        } else {
          this.suggestGate.cancel()
          this.suggestions = []
          this.suggestionsOpen = false
        }
      }, 300),
      immediate: false
    },
    // TB-107: a new search from the header or dashboard reuses this page
    '$route' (to, from) {
      if (to.name !== 'search') return
      if (to.params.searchText === from.params.searchText && (to.query.loc || '') === (from.query.loc || '')) return
      this.locationFilter = typeof to.query.loc === 'string' ? to.query.loc : ''
      this.searchDoctor(to.params.searchText)
    },
    activeTab (val) {
      if (val === 'map') this.$nextTick(() => { this.fitMapToPins(); this.loadMapNext() })
    },
    markers () {
      if (this.activeTab === 'map') this.$nextTick(() => { this.fitMapToPins(); this.loadMapNext() })
    },
    time(val) {
      if (val && val.hh && val.mm && val.A) {
        // TB-123 (W26): clinic wall time inside the default working window (end inclusive)
        const picked = toMin(wallTime(`${val.hh}:${val.mm} ${val.A}`))
        if (picked === null || picked < DEFAULT_WINDOW.from || picked > DEFAULT_WINDOW.to) {
          this.time = null
          this.timeKey++
          this.displayError(`Please select time slots between ${formatClock(fromMin(DEFAULT_WINDOW.from))} to ${formatClock(fromMin(DEFAULT_WINDOW.to))} (${zoneLabel()} time)`)
        }
      }
    }
  },

  components: {
    flatPickr,
    GMapMap,
    GMapMarker,
    GMapInfoWindow,
    MapPinCard,
    TimePicker,
    DoctorResultCard
  },
  computed: {
    // Reactive on resolvedTheme; GMapMap calls map.setOptions() when this changes.
    mapOptions() {
      return { ...BASE_MAP_OPTIONS, ...mapStylesFor(resolvedTheme.value) }
    },
    locationStore () {
      return useLocationStore()
    },
    resultsSummary () {
      const n = this.doctors.length
      const doctors = `${n} ${n === 1 ? 'doctor' : 'doctors'}`
      const term = normalizeSearchTerm(this.search)
      const where = this.locationFilter ? ` near ${this.locationFilter}` : ''
      return term ? `${doctors} for "${term}"${where}` : `All doctors${where}: ${doctors}, soonest available first`
    },
    selectedLabel() {
      if (!this.selectedSort) return "Sort";
      return `Sort: ${this.selectedSort.charAt(0).toUpperCase() + this.selectedSort.slice(1)}`;
    },
    store() {
      return useNavigationStore()
    },
    // TB-109-C: one entry per doctor with a pin, in result order
    mapDoctors () {
      const seen = new Set()
      return (this.markers || []).filter((m) => m && m.info && !seen.has(m.id) && seen.add(m.id)).map((m) => ({ id: m.id, info: m.info }))
    },
    mapSummary () {
      const n = this.mapDoctors.length
      return `${n} ${n === 1 ? 'doctor' : 'doctors'} on the map. Tap a pin or a name for the next free time.`
    },
    activeMapDoctorId () {
      const m = this.infoWinOpen && this.currentMidx !== null ? this.markers[this.currentMidx] : null
      return m ? m.id : ''
    },
    cleanedMarkers() {
      try {
        return (this.markers || []).map((m) => {
          const pos = m && m.position ? m.position : {}
          return {
            ...m,
            position: {
              lat: Number(pos.lat),
              lng: Number(pos.lng)
            }
          }
        })
      } catch (e) {
        return this.markers || []
      }
    },
    cleanedCenter() {
      try {
        const c = this.center || this.locationStore.coords
        return {
          lat: Number(c.lat),
          lng: Number(c.lng)
        }
      } catch (e) {
        return this.center
      }
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    logEvent(analytics, "patient_search_view");
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    this.searchGate.cancel()
    this.suggestGate.cancel()
  },
  created() {
    this.center = { ...this.locationStore.coords }
    this.watchPatientLocation()
    this.suppressSuggestions = true
    this.locationFilter = typeof this.$route.query.loc === 'string' ? this.$route.query.loc : ''
    const maybePromise = this.searchDoctor(this.$route.params.searchText)
    if (maybePromise && typeof maybePromise.finally === 'function') {
      maybePromise.finally(() => {
        this.suppressSuggestions = false
      })
    } else {
      this.suppressSuggestions = false
    }
    this.getConsultationTypes()
  },
  methods: {
    markerName (info) {
      return doctorTitle(personName(info)) || (info && info.name) || ''
    },
    clearLocation () {
      this.locationFilter = ''
      const route = { name: 'search', params: { searchText: this.$route.params.searchText || ' ' } }
      this.$router.replace(route).catch(() => {})
    },
    showAllDoctors () {
      this.search = ''
      this.locationFilter = ''
      this.$router.push({ name: 'search', params: { searchText: ' ' } }).catch(() => {})
      if (this.$route.params.searchText === ' ' && !this.$route.query.loc) this.searchDoctor(' ')
    },
    // TB-107: show every pin; Bahrain stays the default when there is none.
    fitMapToPins () {
      const map = this.$refs.gmapRef
      const bounds = pinBounds(this.markers)
      if (!map || !map.$mapPromise || !bounds) return
      map.$mapPromise
        .then((m) => {
          if (bounds.north === bounds.south && bounds.east === bounds.west) {
            m.setCenter({ lat: bounds.north, lng: bounds.east })
            m.setZoom(MAX_FIT_ZOOM)
            return
          }
          m.fitBounds(bounds, 48)
          const once = m.addListener('idle', () => {
            if (m.getZoom() > MAX_FIT_ZOOM) m.setZoom(MAX_FIT_ZOOM)
            once.remove()
          })
        })
        .catch(() => {})
    },
    // TB-063: search starts on the stored fix or Bahrain; ask for a fresh fix
    // (prompting only when the permission is still unknown) and search again
    // if the position changed.
    watchPatientLocation () {
      const store = this.locationStore
      const before = { ...store.coords }
      // TB-161: the saved address pin wins over the device location.
      Promise.resolve(store.loadHome())
        .then(() => store.init())
        .then((permission) => {
          if (store.hasHome) return store.coords
          if (permission === 'denied' || permission === 'unsupported') return null
          return store.request()
        })
        .then((coords) => {
          if (!coords) return
          if (coords.lat === before.lat && coords.lng === before.lng) return
          this.center = { ...coords }
          this.searchDoctor(this.search)
        })
        .catch(() => {})
    },
    clearSort() {
      this.selectedSort = "availability";
      this.searchDoctor(this.search)
    },

    toggleDropdown() {
      this.open = !this.open;
    },
    selectOption(option) {
      this.selectedSort = option;
      this.open = false;
      this.searchDoctor(this.search)
    },
    handleClickOutside(e) {
      if (this.$refs.dropdownRef && !this.$refs.dropdownRef.contains(e.target)) {
        this.open = false;
      }
    },
    clickOnSearch() {
      if (this.search.trim().length === 0) {
        this.suggestionsOpen = false
        return
      }
      this.suggestionsOpen = false
      this.searchDoctor(this.search)
    },
    goToEstDetails(id) {
      this.$router.push({ name: 'est-profile', query: { estId: id } })
      this.suggestionsOpen = false
    },
    goToProviderDetails(id) {
      this.$router.push({
        name: 'dr-profile',
        query: { doctorId: id }
      })
      this.suggestionsOpen = false
    },
    goToDetails(id) {
      this.$router.push({ name: 'dr-profile', query: { doctorId: id } })
    },
    formatDate(date) {
      return moment(date).format('YYYY-MM-DD')
    },
    formatTime(date) {
      return moment(date).format('ll')
    },
    formatTime1(date) {
      return moment(date).format('h:mm A')
    },
    formatTime2(date) {
      return moment(date).format('h:mm')
    },
    clear() {
      this.time = { hh: '', mm: '', A: '' }
      this.timeKey++
      this.time = null
      this.selectedID = []
      this.selectedconsultationID = []
      this.date = null
      this.isFilter = false
    },
    selectedPoc(ID) {
      if (this.selectedID.includes(ID)) {
        this.selectedID = this.selectedID.filter((i) => i !== ID)
      } else {
        this.selectedID.push(ID)
      }
    },
    selectConsultationType(ID) {
      if (this.selectedconsultationID.includes(ID)) {
        this.selectedconsultationID = this.selectedconsultationID.filter(
          (i) => i !== ID
        )
      } else {
        this.selectedconsultationID.push(ID)
      }
    },

    openFilterPopup() {
      // TB-117: the purposes list (12 KB) is only needed by the filter popup
      this.getPos()
      this.suggestionsOpen = false
      this.isFilter = true
      this.filterPopup = true
    },
    getPos() {
      if (this.posRequested) return
      this.posRequested = true
      lookupGet(this.$http, process.env.VUE_APP_PROVIDERS_SERVICE_API + 'poc')
        .then((res) => {
          this.purpose_of_consultaion = res.data.data
        })
        .catch((err) => {
          this.posRequested = false
          this.displayError(err?.response?.data?.message)
        })
    },
    getConsultationTypes() {
      lookupGet(this.$http, process.env.VUE_APP_PROVIDERS_SERVICE_API + 'consultation-types')
        .then((res) => {
          this.consultationTypes = activeVisitTypes(res.data.data)
        })
        .catch((err) => {
          this.displayError(err?.response?.data?.message)
        })
    },
    // TB-109: free slots of one doctor on one date, with the search filters applied
    fetchSlotsFor (doctor, iso) {
      const params = new URLSearchParams()
      params.append('date', iso)
      ;(this.selectedconsultationID || []).forEach((id) => params.append('consultationTypeIds', id))
      ;(this.selectedID || []).forEach((id) => params.append('pocIds', id))
      const hhmm = this.time ? wallTime(`${this.time.hh}:${this.time.mm} ${this.time.A}`) : ''
      if (hhmm) params.append('time', `${hhmm}:00`)
      return this.$http
        .get(`${process.env.VUE_APP_PROVIDERS_SERVICE_API}schedule/slots/${encodeURIComponent(doctor.id)}?${params.toString()}`)
        .then((res) => (Array.isArray(res?.data?.data) ? res.data.data : []))
        .catch((e) => {
          if (isNoSlotsError(e)) return []
          throw e
        })
    },
    // TB-109: a card asks for its next free times when it scrolls into view (max 4 requests at once)
    loadNext (doctor) {
      const cur = this.nextById[doctor.id]
      if (cur && cur.status !== 'idle') return
      const dates = (doctor.availableDates || []).map((d) => toIsoDate(d.availableDates))
      if (!dates.length) {
        this.nextById = { ...this.nextById, [doctor.id]: { status: 'done', date: '', slots: [] } }
        return
      }
      this.nextById = { ...this.nextById, [doctor.id]: { status: 'loading', date: '', slots: [] } }
      const searchId = this.searchSeq
      this.slotLimiter
        .run(() => findNextAvailable(dates, (iso) => this.fetchSlotsFor(doctor, iso), { maxDays: 3 }))
        .then((res) => {
          if (searchId !== this.searchSeq) return
          const slots = res && res.date ? groupSlots(res.slots).flatMap((g) => g.slots) : []
          this.nextById = { ...this.nextById, [doctor.id]: { status: 'done', date: (res && res.date) || '', slots } }
        })
        .catch(() => {
          if (searchId !== this.searchSeq) return
          this.nextById = { ...this.nextById, [doctor.id]: { status: 'error', date: '', slots: [] } }
        })
    },
    cardFor (item) {
      const detail = item.detail || {}
      const join = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : (v || ''))
      const amount = Number(item.perSlotAmount)
      return {
        id: item.id,
        name: doctorTitle(personName(item)) || item.name || 'Doctor',
        specialities: join(detail.specaillizesIn),
        qualifications: join(detail.qualifications),
        avatar: item.avatar,
        rating: item.rating || 0,
        distance: item.nearestDistance,
        distanceFromCentre: this.locationStore.isDefault,
        price: Number.isFinite(amount) && amount > 0 ? formatMoney(amount) : ''
      }
    },
    estSubtitle (est) {
      const spec = est?.detail?.specaillizesIn
      if (Array.isArray(spec) && spec.length) return spec.join(', ')
      return [est?.address, est?.city].filter(Boolean).join(', ')
    },
    openDoctor (id, { date, slot } = {}) {
      const query = { doctorId: id }
      if (date) query.date = date
      if (slot) query.slot = slot
      this.$router.push({ name: 'dr-profile', query })
    },
    // getNextWeek(index, item) {
    //   const current = _.last(item.weeks).date
    //   const week = new Array()
    //   current.setDate(current.getDate() - current.getDay() + 1)
    //   for (let i = 0; i < 7; i++) {
    //     week.push({ date: new Date(current), dot: 0 })
    //     current.setDate(current.getDate() + 1)
    //   }
    //   item.weeks = week
    // },
    // getPrevoiusWeek(index, item) {
    //   const current = item.weeks[0].date
    //   if (
    //     new Date().getDate() >= Number(current.getDate()) + 6 ||
    //     Number(current.getDate()) + 6 > 29
    //   ) {
    //     const week = new Array()
    //     current.setDate(current.getDate() - current.getDay())
    //     for (let i = 0; i < 7; i++) {
    //       week.push({ date: new Date(current), dot: 0 })
    //       current.setDate(current.getDate() - 1)
    //     }
    //     item.weeks = week.reverse()
    //   }
    // },
    getWeeks(current) {
      let week = new Array()
      current.setDate(current.getDate() - current.getDay() + 1)
      for (let i = 0; i < 7; i++) {
        week.push({ date: new Date(current), dot: 0 })
        current.setDate(current.getDate() + 1)
      }
      this.weeks = week
      this.doctors.map((mainItem, index) => {
        let weekUpdate = week

        if ('schedule' in mainItem && mainItem.schedule != null) {
          mainItem.schedule.map((item) => {
            weekUpdate = week.map((t) => {
              let dot = 0
              if (formatTime(t.date) == formatTime(item.consultationDate)) {
                if (item.status) {
                  dot = 1
                } else {
                  dot = 2
                }
              }
              return {
                date: new Date(t.date),
                dot: dot
              }
            })
          })
          // mutate the existing object to preserve reactivity references
          mainItem.weeks = weekUpdate
        } else {
          mainItem.weeks = week
        }
      })
    },
    searchDoctor(search) {
      if (search == undefined) {
        this.search = ''
      } else {
        this.search = search.trim() ? search.toLowerCase() : ''
      }
      this.lastSearchedTerm = this.search

      // TB-107: "Dr Saji" searches "saji"; a blank search lists every doctor.
      // Only the latest request may answer and the spinner always stops.
      const gate = this.searchGate.next()
      this.searchState = 'loading'
      this.searchSeq = (this.searchSeq || 0) + 1
      this.slotLimiter.clear()
      this.nextById = {}
      const body = {
        search: normalizeSearchTerm(this.search) || ' ',
        isFilter: this.isFilter,
        date: this.date || '',
        time: this.time && wallTime(`${this.time?.hh}:${this.time?.mm} ${this.time?.A}`)
          ? `${wallTime(`${this.time.hh}:${this.time.mm} ${this.time.A}`)}:00`
          : '',
        consultationTypeIds: this.selectedconsultationID,
        pocIds: this.selectedID,
        // Always numbers: the fix or the Bahrain centre, never 0,0
        lat: this.locationStore.coords.lat,
        lng: this.locationStore.coords.lng,
        sortBy: this.selectedSort || 'availability'
      }
      if (this.locationFilter) body.location = this.locationFilter
      return this.$http
        .post(`${process.env.VUE_APP_PROVIDERS_SERVICE_API}search`, body, { signal: gate.signal })
        .then((res) => {
          if (!gate.isCurrent()) return
          this.suggestionsOpen = false
          this.filterPopup = false
          // this.isFilter = false
          var doctors = []
          const noDates = []
          const results = Array.isArray(res?.data?.data) ? res.data.data : []
          this.establishments = results.filter((item) => item.name) || []
          const { pins, withoutLocation } = mapPinsFor(results)
          // establishments are not shown in the no-location note
          this.withoutLocation = withoutLocation
          results.map((mainItem, docIndex) => {
            try {
              const hasDetail = 'detail' in mainItem && mainItem.detail != null
              const hasLocations =
                hasDetail &&
                Array.isArray(mainItem.detail.locations) &&
                mainItem.detail.locations.length > 0
              const loc = hasLocations ? mainItem.detail.locations[0] : null
              const locLat = loc && (loc.latitude || loc.lat)
              const locLng = loc && (loc.longitude || loc.lng)
            } catch (e) { }
            let availableDates = []
            if ('schedule' in mainItem && mainItem.schedule != null) {
              let output = []
              mainItem.schedule.forEach(function (filterItem) {
                let existing = output.filter(function (v, i) {
                  return v.consultationDate == filterItem.consultationDate
                })
                if (existing?.length) {
                  let existingIndex = output.indexOf(existing[0])
                  output[existingIndex].slots = output[
                    existingIndex
                  ].slots.concat(filterItem.slots)
                } else {
                  if (typeof filterItem.slots == 'string')
                    filterItem.slots = [filterItem.slots]
                  output.push(filterItem)
                }
              })
              mainItem.schedule = output
            }
            // Unified location detection: check detail.locations, top-level location(s), or locations
            let locArr = []
            if (mainItem?.detail && Array.isArray(mainItem.location))
              locArr = mainItem.location
            else if (Array.isArray(mainItem.location))
              locArr = mainItem.location
            else if (
              mainItem?.location &&
              typeof mainItem.location === 'object'
            )
              locArr = [mainItem.location]

            if (Array.isArray(locArr) && locArr.length) {
              // Only calculate distance if we have user coords and valid location coords
              const userLat = this.locationStore.coords.lat
              const userLng = this.locationStore.coords.lng

              const loc = locArr[0]
              const rawLat = loc ? loc.latitude ?? loc.lat ?? null : null
              const rawLng = loc ? loc.longitude ?? loc.lng ?? null : null
              const locLat = rawLat != null ? Number(rawLat) : null
              const locLng = rawLng != null ? Number(rawLng) : null
              const userLatNum = userLat != null ? Number(userLat) : null
              const userLngNum = userLng != null ? Number(userLng) : null

              if (
                userLatNum != null &&
                userLngNum != null &&
                locLat != null &&
                locLng != null
              ) {
                calcCrow(userLatNum, userLngNum, locLat, locLng)
              }
              function calcCrow(lat1, lon1, lat2, lon2) {
                var R = 6371
                var dLat = toRad(lat2 - lat1)
                var dLon = toRad(lon2 - lon1)
                var lat1r = toRad(lat1)
                var lat2r = toRad(lat2)

                var a =
                  Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.sin(dLon / 2) *
                  Math.sin(dLon / 2) *
                  Math.cos(lat1r) *
                  Math.cos(lat2r)
                var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
                var d = R * c
                mainItem['distance'] = d
              }
              function toRad(Value) {
                return (Value * Math.PI) / 180
              }
            } else {
              mainItem['distance'] = 0
            }
            if ('schedule' in mainItem && mainItem.schedule != null) {
              mainItem.schedule.map((item, index) => {
                // If a global date filter is active, only include matching dates
                if (this.isFilter && this.date) {
                  if (
                    this.formatDate(item.consultationDate) ==
                    this.formatDate(this.date)
                  ) {
                    availableDates.push({
                      availableDates: item.consultationDate
                    })
                  }
                } else {
                  availableDates.push({ availableDates: item.consultationDate })
                }
              })
            }
            mainItem.availableDates = availableDates
            // TB-109: doctors without dates stay listed (after the rest) unless a date filter is on
            if (availableDates.length) doctors.push(mainItem)
            else if (!this.isFilter && !mainItem.name) noDates.push(mainItem)
          })

          this.markers = pins
          this.doctors = doctors.concat(noDates)
          // this.getWeeks(new Date())
          this.searchState = 'done'
        })
        .catch((err) => {
          if (!gate.isCurrent() || isCancelled(err)) return
          this.doctors = []
          this.establishments = []
          this.markers = []
          this.withoutLocation = []
          this.searchState = 'error'
        })
    },
    toggleInfoWindow(idx) {
      if (this.currentMidx === idx) {
        this.infoWinOpen = !this.infoWinOpen
      } else {
        this.currentMidx = idx
        this.infoWinOpen = true
      }
      const m = this.markers[idx]
      if (this.infoWinOpen && m && m.info) this.loadNext(m.info)
    },
    // TB-109-C: a row in the list beside the map opens that doctor's pin
    focusPin (id) {
      const idx = this.markers.findIndex((m) => m.id === id)
      if (idx < 0) return
      this.currentMidx = idx
      this.infoWinOpen = true
      this.loadNext(this.markers[idx].info)
      const pos = this.cleanedMarkers[idx].position
      const map = this.$refs.gmapRef
      if (map && map.$mapPromise) map.$mapPromise.then((m) => m.panTo(pos)).catch(() => {})
    },
    loadMapNext () {
      this.mapDoctors.forEach((d) => this.loadNext(d.info))
    }
    // toggleInfoWindow(marker, idx) {
    //    if (!marker || !marker.position) return
    //   if (this.currentMidx == idx) {
    //     this.infoWinOpen = !this.infoWinOpen
    //   }
    //   else {
    //      this.infoWindowPos = marker.position
    //     this.infoContent = marker
    //     this.infoWinOpen = true
    //     this.currentMidx = idx
    //   }
    // }
  }
}
</script>

<style>
/* TB-109: result cards */
.tb-results { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 26rem), 1fr)); gap: 1.25rem; }
.tb-results-summary { color: var(--tb-text-muted); font-size: 0.9rem; font-weight: 600; }
.tb-skel-card { padding: 1.25rem; border-radius: var(--tb-radius-card); background: var(--tb-bg-surface); border: 1px solid var(--tb-border); }
.tb-skel {
  display: block; height: 0.8rem; border-radius: 6px;
  background: linear-gradient(90deg, var(--tb-bg-muted) 25%, var(--tb-bg-subtle) 50%, var(--tb-bg-muted) 75%);
  background-size: 200% 100%; animation: tb-skel-shimmer 1.2s ease-in-out infinite;
}
.tb-skel--avatar { width: 4rem; height: 4rem; border-radius: 50%; flex: none; }
@keyframes tb-skel-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
@media (prefers-reduced-motion: reduce) { .tb-skel { animation: none; } }
.tb-est-card {
  display: flex; align-items: center; gap: 1rem; padding: 1.1rem 1.25rem; min-width: 0;
  border-radius: var(--tb-radius-card); background: var(--tb-bg-surface); border: 1px solid var(--tb-border); box-shadow: var(--tb-shadow-sm);
}
.tb-est-card__avatar { width: 3.25rem; height: 3.25rem; border-radius: 50%; object-fit: cover; flex: none; }
.tb-est-card__name { margin: 0; font-size: 1rem; font-weight: 800; color: var(--tb-text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tb-est-card__sub { margin: 0.1rem 0 0; font-size: 0.85rem; color: var(--tb-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tb-est-card__view {
  flex: none; padding: 0.55rem 1.2rem; border-radius: 999px; border: 1px solid var(--tb-primary);
  background: var(--tb-bg-surface); color: var(--tb-primary-text); font-weight: 800; font-size: 0.85rem; cursor: pointer;
}
/* TB-109-C: map beside a compact doctor list (list under the map on phones) */
.tb-map-layout { display: grid; grid-template-columns: minmax(0, 1fr) 20rem; gap: 1rem; align-items: start; }
.tb-map-list {
  list-style: none; margin: 0; padding: 0.5rem; display: flex; flex-direction: column; gap: 0.25rem;
  max-height: min(560px, 65vh); overflow-y: auto;
  border-radius: var(--tb-radius-card); background: var(--tb-bg-surface); border: 1px solid var(--tb-border); box-shadow: var(--tb-shadow-sm);
}
.tb-map-list__item { border-radius: var(--tb-radius-control); }
.tb-map-list__item:focus-visible { outline: none; box-shadow: var(--tb-shadow-focus); }
.tb-map-note { padding: 1rem 1.25rem; border-radius: var(--tb-radius-card); background: var(--tb-bg-surface); border: 1px solid var(--tb-border); color: var(--tb-text-muted); }
.tb-map-note__doc {
  padding: 0.3rem 0.8rem; border-radius: 999px; border: 1px solid var(--tb-border); background: var(--tb-bg-surface);
  color: var(--tb-primary-text); font: inherit; font-size: 0.85rem; font-weight: 700; cursor: pointer;
}
.tb-map-note__doc:hover { border-color: var(--tb-primary); }
/* Google's info window chrome: Clinical Calm radius and shadow */
.tb-search-map .gm-style .gm-style-iw-c { border-radius: var(--tb-radius-card); box-shadow: var(--tb-shadow-md); padding: 0.75rem !important; }
.tb-search-map .gm-style .gm-style-iw-d { overflow: hidden !important; }
@media (max-width: 1023px) {
  .tb-map-layout { grid-template-columns: minmax(0, 1fr); }
  .tb-map-list { max-height: none; }
}
/* TB-063: the search map framed like the picker map */
.tb-search-map {
  width: 100%;
  border: 1px solid var(--tb-border);
  border-radius: var(--tb-radius-card);
  overflow: hidden;
  background: var(--tb-bg-muted);
}

.sort-btn {
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.vue__time-picker .controls .char {
  padding-top: 4px;
  font-size: 1.7em;
  line-height: 100%;
  -webkit-margin-before: -0.15em;
  color: red;
  right: 77px !important;
}

.vue__time-picker {
  display: inline-block;
  position: relative;
  font-size: 1em;
  width: 12em;
  font-family: sans-serif;
  vertical-align: middle;
}

.vue__time-picker .dropdown {
  margin-top: 18px;
  background: var(--tb-bg-elevated);
  width: 203px !important;
}

.vue__time-picker input.display-time {
  box-shadow: var(--tb-shadow-sm);
  width: 15em;
  height: 2.2em;
  font-size: 1.2em;
  margin-top: 10px;
  border: none;
  background-color: var(--tb-bg-surface);
  border-radius: 50% !important;
}

.buildingBg {
  background-image: url('../../assets/images/buildings-bg.png');
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center bottom;
}

.result-tabs ul.ul-tabs.vs-tabs--ul.ul-tabs-center {
  background-color: var(--tb-bg-surface);
  width: 40%;
  border-radius: 50px;
  box-shadow: 0 4px 8px 0 rgb(0 0 0 / 12%), 0 2px 4px 0 rgb(0 0 0 / 8%) !important;
  margin: 10px;
  padding: 5px;
}

.result-tabs ul.ul-tabs.vs-tabs--ul.ul-tabs-center li .vs-tabs--btn {
  padding: 10px 65px;
  background-color: var(--tb-bg-surface);
  border-radius: 50px;
}

.result-tabs ul.ul-tabs.vs-tabs--ul.ul-tabs-center li {
  width: 50%;
}

.result-tabs ul.ul-tabs.vs-tabs--ul.ul-tabs-center li.activeChild .vs-tabs--btn {
  color: white !important;
  background-image: var(--tb-primary-fill-gradient) !important;
}

.result-tabs span.line-vs-tabs {
  display: none;
}

.result-tabs .con-ul-tabs {
  display: -ms-flexbox;
  display: flex;
  -ms-flex-pack: center;
  justify-content: center;
}

.result-tabs .con-ul-tabs:before {
  content: ' ';
  width: 100%;
  position: absolute;
  height: 1px;
  top: 40px;
  background-image: var(--tb-primary-fill-gradient) !important;
  opacity: 0.5;
}

.schedule-tabs .con-ul-tabs:before {
  content: none;
}

.schedule-tabs ul.ul-tabs.vs-tabs--ul {
  border-top: 2px solid rgb(128 128 128 / 15%);
  border-bottom: 2px solid rgb(128 128 128 / 15%);
  padding: 5px 0px;
}

.schedule-tabs ul.ul-tabs.vs-tabs--ul li.vs-tabs--li:first-child {
  border-right: 2px solid rgb(128 128 128 / 15%);
}

.schedule-tabs ul.ul-tabs.vs-tabs--ul li.vs-tabs--li.activeChild::before {
  content: ' ';
  width: 100%;
  height: 2px;
  background-color: var(--tb-primary-text);
  position: absolute;
  bottom: -6px;
}

.schedule-tabs ul.ul-tabs.vs-tabs--ul li.vs-tabs--li.activeChild::after {
  content: ' ';
  position: absolute;
  bottom: -17px;
  left: 45%;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 12px solid var(--tb-primary-text);
}

.for-scroll::-webkit-scrollbar {
  height: 10px;
  width: 10px;
  /* width of the entire scrollbar */
}

.for-scroll::-webkit-scrollbar-track {
  background: var(--tb-bg-subtle);
  /* color of the tracking area */
}

.for-scroll::-webkit-scrollbar-thumb {
  background-color: #48c2fc;
  border-radius: 45px;
  border: 3px solid #4ac3fc;
}

element.style {
  overflow: scroll;
  max-height: 316px;
}

/* TB-109-C: the pin popup is a Clinical Calm surface card (was a white-on-gradient bubble) */
.gm-style-iw.gm-style-iw-c .gm-style-iw-d {
  color: var(--tb-text-body);
  background-color: transparent;
  overflow: hidden !important;
  padding: 0 0.25rem 0.25rem;
}

.gm-style-iw.gm-style-iw-c {
  background: var(--tb-bg-surface);
  padding: 0;
}
.gm-style .gm-style-iw-tc::after { background: var(--tb-bg-surface); }

/* iPhone x */
@media only screen and (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) {
  .con-ul-tabs ul {
    width: auto !important;
  }

  .con-ul-tabs ul .vs-tabs--li button {
    font-size: 14px;
  }

  .result-tabs ul.ul-tabs.vs-tabs--ul.ul-tabs-center li .vs-tabs--btn {
    padding: 8px 40px;
  }
}
</style>

<style less-scoped>
.con-vs-popup .vs-popup {
  width: 560px !important;
}
</style>
